import { useCallback, useState } from 'react';

/**
 * useState for a page's tab that survives a refresh: the choice is kept in
 * sessionStorage (this browser tab only), so reloading puts you back on the
 * same tab, while a new browser tab starts on the default.
 *
 *   const [tab, setTab] = usePersistentTab('tradeWatcher.tab', 'board', ['board', 'connections']);
 *
 * key      unique per page/tab bar
 * initial  default value (or a function returning it)
 * allowed  optional list of valid values; anything else falls back to initial
 *          (e.g. a tab the person can no longer see)
 */
const PREFIX = 'riskloTab:';

function read(key, allowed) {
  try {
    const v = sessionStorage.getItem(PREFIX + key);
    if (v === null) return undefined;
    const parsed = JSON.parse(v);
    if (Array.isArray(allowed) && !allowed.includes(parsed)) return undefined;
    return parsed;
  } catch (_e) {
    return undefined;
  }
}

export function readPersistentTab(key, allowed) {
  return read(key, allowed);
}

export default function usePersistentTab(key, initial, allowed) {
  const [value, setValue] = useState(() => {
    const saved = read(key, allowed);
    if (saved !== undefined) return saved;
    return typeof initial === 'function' ? initial() : initial;
  });
  const set = useCallback((next) => {
    setValue((cur) => {
      const v = typeof next === 'function' ? next(cur) : next;
      try { sessionStorage.setItem(PREFIX + key, JSON.stringify(v)); } catch (_e) { /* still works, just not across refresh */ }
      return v;
    });
  }, [key]);
  return [value, set];
}
